import { type components } from '@/generated/internal-api/openapi-types';
export type ReservedSampleApiId = components['schemas']['ReservedSampleApiId.schema'];
import { type Brand, type ID } from '@/model/Common.model';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import { type StudyApiId } from '@/model/Study.model';
import {
  type SampleDefinitionV1,
  type PlannedAnimalTarget,
  type RegisteredAnimalTarget,
} from '@/model/SamplePlan.model';
import type { MetadataGlossaryApiId } from '@/model/Metadata.model.ts';

export type StaticGroupDimension = Exclude<components['schemas']['ReservedSampleGroupDimension.schema'], 'sampleMeta'>;

export type MetaGroupDimension = { readonly type: 'sampleMeta'; readonly apiId: MetadataGlossaryApiId };
export type GroupDimension = StaticGroupDimension | MetaGroupDimension;

export const isMetaGroupDimension = (d: GroupDimension): d is MetaGroupDimension =>
  typeof d === 'object' && d.type === 'sampleMeta';

export const DEFAULT_GROUPINGS: Array<GroupDimension> = ['sampleDefinition', 'studyGroup'];

export interface MetaField {
  apiId: MetadataGlossaryApiId;
  title: string;
}

export const STATIC_DIMENSION_LABELS: Record<StaticGroupDimension, string> = {
  sampleDefinition: 'Sample Definition',
  studyGroup: 'Study Group',
  plannedAnimal: 'Planned Animal',
  animal: 'Animal',
  timepoint: 'Timepoint',
  sampleType: 'Sample Type',
};

export const MAX_CHIPS = 4;

export type SamplePlan = components['schemas']['SamplePlanV1.schema'];
export type ReservedSampleAggregation = components['schemas']['ReservedSampleAggregationV1.schema'];
export type ReservedSample = components['schemas']['ReservedSampleV1.schema'];
export type MetadataGlossaryV1 = components['schemas']['MetadataGlossaryV1.schema'];
export type ReservedSampleNullableDimension = components['schemas']['ReservedSampleNullableDimension.schema'];

export type SampleDefinitionMap = Map<string, SampleDefinitionV1>;
export type GroupMap = Map<GroupApiId, TreatmentGroup>;
export type GroupByNumberMap = Map<number, TreatmentGroup>;
export type SampleTypeMap = Map<string, string>;
export type AnimalMap = Map<
  PlannedAnimalTarget['api_id'] | RegisteredAnimalTarget['api_id'],
  ({ type: 'planned' } & PlannedAnimalTarget) | ({ type: 'registered' } & RegisteredAnimalTarget)
>;

export type ChildData<T> = { status: 'loading' } | { status: 'loaded'; data: T } | { status: 'error'; error: unknown };

export type RootKey = Brand<string, 'RootKey'>;
export type Level1Key = Brand<string, 'Level1Key'>;
export type Level2Key = Brand<string, 'Level2Key'>;

/** Brands a raw aggregation key string as a RootKey (single segment). */
export const toRootKey = (key: string): RootKey => key as RootKey;
/** Brands a two-segment compound key as a Level1Key. */
export const toLevel1Key = (rootKey: RootKey, key: string): Level1Key => `${rootKey}:${key}` as Level1Key;
/** Brands a three-segment compound key as a Level2Key. */
export const toLevel2Key = (rootKey: RootKey, level1Key: string, level2Key: string): Level2Key =>
  `${rootKey}:${level1Key}:${level2Key}` as Level2Key;

export type LeafKey = RootKey | Level1Key | Level2Key;

export type TreeStaticColumnId = 'name' | 'group' | 'plannedAnimal' | 'animal' | 'status' | 'timepoint' | 'sampleType';

export type SampleNamesTableContext = {
  studyApiId: StudyApiId;
  studyId: ID;
  groupMap: GroupMap;
  groupByNumberMap: GroupByNumberMap;
  animalCountByGroup: Record<string, number>;
  groupings: Array<GroupDimension>;
  metaFields: Array<MetaField>;
  sampleTypeMap: SampleTypeMap;
  animalMap: AnimalMap;
  sampleDefinitionMap: SampleDefinitionMap;
  openRoots: Set<RootKey>;
  openLevel1: Set<Level1Key>;
  openLevel2: Set<Level2Key>;
  level1Children: Map<RootKey, ChildData<Array<ReservedSampleAggregation>>>;
  level2Children: Map<Level1Key, ChildData<Array<ReservedSampleAggregation>>>;
  leafNames: Map<LeafKey, ChildData<Array<ReservedSample>>>;
  /** Static column IDs that are currently visible; opt-out (all visible unless explicitly set to false). */
  visibleStaticColumnIds: ReadonlyArray<TreeStaticColumnId>;
  /** Metadata fields that are currently visible; opt-in (hidden by default, visible when set to true). */
  visibleMetaColumns: ReadonlyArray<MetaField>;
  /** Precomputed metadata display strings keyed by composite row cache key, then by MetaField apiId. */
  precomputedMetadata: Map<string, Map<string, string>>;
  toggleRoot: (key: RootKey) => void;
  toggleLevel1: (rootKey: RootKey, level1KeyRaw: string) => void;
  toggleLevel2: (rootKey: RootKey, level1KeyRaw: string, level2KeyRaw: string) => void;
  selectedReservedSampleIds: Set<ReservedSampleApiId>;

  toggleReservedSampleIds: (ids: Array<ReservedSampleApiId>) => void;
};

export interface ExecuteReservedSamplesLocationState {
  studyApiId?: StudyApiId;
  reservedSampleApiIds?: Array<ReservedSampleApiId>;
  hasCalculateCollectionTime?: boolean;
}
